import { Avatar, AvatarGroup, Box, Button, Card, IconButton, Slide, Typography, useTheme } from "@mui/material"
import { observer } from "mobx-react"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { useMeetingStore } from "../../store/meeting.store"
import { getNameInitials, Icons, USER_CARD_COLOR_SETS } from "../../utils/utilities"
import { toast } from 'react-toastify'


const NewRequestsList = observer( () => {
    const [handRaisesToastOpen, setHandRaisesToastOpen] = useState( false )
    const [newJoinsToastOpen, setNewJoinsToastOpen] = useState( false )
    const [newJoinsDetails, setNewJoinsDetails] = useState( { message: "", userCount: 0, newJoins: [] } )
    const MeetingStore = useMeetingStore()
    const { palette } = useTheme()

    const lastHandRaisesCount = useRef( 0 )

    const handleClose = useCallback( () => {
        setHandRaisesToastOpen( false )
        setNewJoinsToastOpen( false )
        MeetingStore.setMeetingRoomLayoutSettings( { hideHandsRaisedCard: true } )
    }, [MeetingStore] )

    const openPendingRequests = () => {
        MeetingStore.setMeetingRoomLayoutSettings( {
            isLeftSideCollapseOpen: true,
            leftSideCollapseTab: "participants",
            hideHandsRaisedCard: true,
        } )
        handleClose()
    }


    useEffect( () => {
        if (
            !MeetingStore.getMeetingRoomLayoutSettings.hideHandsRaisedCard &&
            MeetingStore.getRaisedHands?.length > 0
        ) {
            if ( MeetingStore.getRaisedHands?.at( -1 )?.user_id !== MeetingStore.getLocalUser.user_id )
                if ( lastHandRaisesCount.current < MeetingStore.getRaisedHands?.length ) {
                    setHandRaisesToastOpen( true )
                    if ( !MeetingStore.getMeetingRoomLayoutSettings?.isLeftSideCollapseOpen || ( MeetingStore.getMeetingRoomLayoutSettings?.isLeftSideCollapseOpen && MeetingStore.getMeetingRoomLayoutSettings?.leftSideCollapseTab !== 'participants' ) )
                        MeetingStore.playNotificationSound()
                }
        } else if ( MeetingStore.getRaisedHands?.length === 0 ) {
            setHandRaisesToastOpen( false )
        }
        lastHandRaisesCount.current = MeetingStore.getRaisedHands?.length
    }, [MeetingStore.getRaisedHands, MeetingStore] )

    useEffect( () => {
        const newJoins = MeetingStore.getNewJoins
        if ( newJoins.length === 0 ) return

        const count = newJoins.length
        const names = newJoins.map( user => user.name ).join( ", " )

        MeetingStore.setNewJoins( [] )

        setNewJoinsDetails( {
            message: count > 4
                ? `${count} new users joined the meeting.`
                : `${names} joined the meeting`,
            userCount: count,
            newJoins
        } )
        setNewJoinsToastOpen( true )

    }, [MeetingStore, MeetingStore.getNewJoins] )

    useEffect( () => {
        if ( !handRaisesToastOpen && !newJoinsToastOpen ) return

        const timer = setTimeout( () => {
            handleClose()
        }, 600000 )

        return () => clearTimeout( timer )
    }, [handRaisesToastOpen, newJoinsToastOpen, handleClose] )

    return (
        <Box
            sx={{
                position: "fixed",
                bottom: 80,
                left: 20,
                zIndex: 1300, // above main content but below dialogs
                display: "flex",
                flexDirection: "column",
                gap: 1,
            }}
        >
            <Slide direction="right" in={handRaisesToastOpen} mountOnEnter unmountOnExit>
                <Card
                    sx={{
                        borderRadius: "10px",
                        pointerEvents: "all",
                        // border: border[1],
                        bgcolor: palette.grey[900],
                        color: palette.common.white,
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        px: 2,
                        py: 1.5,
                        minWidth: "180px",
                        maxWidth: "350px",
                        width: "90%",
                        boxShadow: 4,
                    }}
                >
                    {Icons.default.FrontHandOutlined}
                    <Typography
                        variant="body2"
                        sx={{ flex: 1, color: palette.common.white }}
                    >
                        {MeetingStore.getRaisedHands?.length > 1
                            ? `${MeetingStore.getRaisedHands?.length} people raised their hands`
                            : `${MeetingStore.getRaisedHands?.[0]?.name || "A user"} raised a hand`}
                    </Typography>
                    <Button sx={{ fontWeight: "500", borderRadius: "20px", borderWidth: "2px" }} variant='outlined' size="small" onClick={openPendingRequests}>
                        View
                    </Button>
                    <IconButton
                        size="small"
                        onClick={handleClose}
                        sx={{ color: palette.grey[400] }}
                    >
                        {Icons.default.CloseIcon}
                    </IconButton>
                </Card>
            </Slide>

            {/* NEW JOINS */}
            <Slide direction="right" in={newJoinsToastOpen} mountOnEnter unmountOnExit>
                <Card
                    sx={{
                        borderRadius: "10px",
                        pointerEvents: "all",
                        bgcolor: palette.grey[900],
                        color: palette.common.white,
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        px: 2,
                        py: 1.5,
                        minWidth: "180px",
                        maxWidth: "450px",
                        width: "90%",
                        boxShadow: 4,
                    }}
                >
                    <Box>
                        <AvatarGroup slotProps={{ surplus: { sx: { borderColor: "white !important", color: "white", width: "30px", height: "30px", } } }} sx={{ borderColor: "white !important", color: "white" }} spacing={20} max={4} >
                            {newJoinsDetails.newJoins.map( user => (
                                <Avatar key={user.name} sx={{ borderColor: "white !important", fontSize: "14px", width: "30px", height: "30px", color: "white", background: USER_CARD_COLOR_SETS[user.color_index || 3].primary }} >{getNameInitials( user?.name )}</Avatar>
                            ) )}
                        </AvatarGroup>
                    </Box>
                    <Typography
                        variant="body2"
                        sx={{ flex: 1, color: palette.common.white }}
                    >{
                            newJoinsDetails.message
                        }
                    </Typography>
                    <Button sx={{ fontWeight: "500", borderRadius: "20px", borderWidth: "2px" }} variant='outlined' size="small" onClick={openPendingRequests}>
                        View
                    </Button>
                    <IconButton
                        size="small"
                        onClick={handleClose}
                        sx={{ color: palette.grey[400] }}
                    >
                        {Icons.default.CloseIcon}
                    </IconButton>
                </Card>
            </Slide>
        </Box>
    )
} )

export default NewRequestsList
